(ns ui.totp.new
  "The UI that let's you add a new totp entity to your storrito account."
  (:require [storrito.ui.button :as button]
            [storrito.ui.core :as ui]
            [parts.replicant.command :as command]
            [ui.app-frame :as app-frame]
            [parts.replicant.query :as query]
            [ui.totp.core :as core]
            ))

(defn panel
  [state]
  (let [uuid (get-in state [:totp/entry :totp/uuid])
        result (query/get-result state core/totp-query)
        totp-entries (get-in result [:totp :totp/entries])
        entry (merge (get totp-entries uuid)
                     (:totp/entry state))
        command {:command/kind :command/totp-add
                 :command/data (dissoc entry :totp/otp)}
        command-action [:data/command
                        command
                        {:on-success [[:data/query core/totp-query]
                                      [:totp/refresh]]}]
        progress (core/calc-expire-time state)]
    [:div.flex.items-center.justify-center
     [:div.bg-white.shadow-xl.rounded-2xl.px-8.pt-10.pb-10.max-w-md.w-full.mx-auto.flex.flex-col.items-center
      [:h2.text-2xl.font-bold.text-gray-900.mb-2.text-center.w-full "Add a new Authenticator (TOTP)"]
      [:p.text-gray-600.mb-6.text-center.w-full "Store a new authenticator key for your account. You can use this to generate 2FA codes for Instagram, Google, and more."]

      [:div.w-full.space-y-5
       [:div
        [:label.text-gray-800.block.text-base.font-bold.mb-1 "Codename"]
        [:input.w-full.px-4.py-2.border.border-gray-300.rounded-md.shadow-sm.focus:outline-none.focus:ring-2.focus:ring-blue-500.focus:border-blue-500.text-gray-800.bg-gray-50.transition-all.duration-200
         {:required true
          :type "text"
          :placeholder "e.g. Instagram, Google, Work Account"
          :value (:totp/name entry)
          :on {:input [[:store/assoc-in [:totp/entry :totp/name] :event/target.value]]
               :keyup [[:js/on-enter] command-action]}}]
        [:p.text-xs.text-gray-500.mt-1 "A name to identify this authenticator."]]

       [:div
        [:label.text-gray-800.block.text-base.font-bold.mb-1 "Secret Key"]
        [:input.w-full.px-4.py-2.border.border-gray-300.rounded-md.shadow-sm.focus:outline-none.focus:ring-2.focus:ring-blue-500.focus:border-blue-500.text-gray-800.bg-gray-50.transition-all.duration-200
         {:required true
          :type "text"
          :placeholder "Paste the secret key here"
          :value (:totp/secret-key entry)
          :on {:input [[:store/assoc-in [:totp/entry :totp/secret-key] :event/target.value]]
               :keyup [[:js/on-enter] command-action]}}]
        [:p.text-xs.text-gray-500.mt-1 "Paste the secret key from your authenticator app or QR code."]]

       [:div
        [:label.text-gray-800.block.text-base.font-bold.mb-1 "Description (optional)"]
        [:input.w-full.px-4.py-2.border.border-gray-300.rounded-md.shadow-sm.focus:outline-none.focus:ring-2.focus:ring-blue-500.focus:border-blue-500.text-gray-800.bg-gray-50.transition-all.duration-200
         {:type "text"
          :maxlength "100"
          :placeholder "e.g. Used for Instagram login on my phone"
          :value (:totp/description entry)
          :on {:input [[:store/assoc-in [:totp/entry :totp/description] :event/target.value]]
               :keyup [[:js/on-enter] command-action]}}]
        [:p.text-xs.text-gray-500.mt-1 "Add a note to remember what this is for (optional)."]]
      ]

      (when-let [otp (:totp/otp entry)]
        [:div.flex.items-center.flex-col.justify-center.m-5
         [:h1.text-base.font-medium.text-gray-700.mb-1 "Your current 2FA Code:"]
         [:h1.text-4xl.font-mono.mb-2 otp]
         [:div.w-20.bg-gray-300.rounded-3xl.h-2.5.ml-4
          [:div {:class "bg-indigo-600 h-2.5 rounded-3xl transition-all"
                 :style {:width (str (* 100 progress) "%")}}]]])

      (cond
        (command/success? state command)
        [:div.flex.items-center.flex-col.justify-center.w-full
         [:div.rounded-md.bg-green-50.p-4.mb-4.w-full
          [:div.flex
           [:div.shrink-0
            [:svg.size-5.text-green-400 {:viewBox "0 0 20 20" :fill "currentColor" :aria-hidden "true" :data-slot "icon"}
             [:path {:fill-rule "evenodd" :d "M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.857-9.809a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z" :clip-rule "evenodd"}]]]
           [:div.ml-3
            [:p.text-sm.font-medium.text-green-800.mb-4 "Successfully saved"]]]]
         [:ui/a.link
          {:ui/location
           {:location/page-id :pages/totp
            :location/params {}}
           :class (cons "ml-2" ui/button-secondary)}
          "Continue"]]

        (command/error? state command)
        [:p {:class "text-red-500 text-s italic min-h-8"}
         "ERROR : "
         (command/get-latest-error state command)]

        :else
        [:div.flex.w-full.space-x-4.mt-8
         [:button.w-full.bg-secondary-900.text-white.font-semibold.py-3.px-6.rounded-lg.shadow-md.hover:bg-blue-800.transition-all.duration-200
          {:on {:click [command-action]}
           :type "button"}
          "Save TOTP"]
         [:ui/a.link.w-full.flex.justify-center.items-center.bg-white.text-blue-900.font-semibold.py-3.px-6.rounded-lg.shadow-md.border.border-secondary-900.hover:bg-gray-100.transition-all.duration-200
          {:ui/location
           {:location/page-id :pages/totp
            :location/params {}}
           :class (cons "ml-2" ui/button-secondary)}
          "Cancel"]])]
    ]))

(defn render
  [state]
  (app-frame/app-frame
    state
    (panel state)))

(def page
  {:page-id :pages/totp-new
   :route [["ui" "totp" "new"]]
   :on-load (fn [location]
              [[:store/assoc :totp/entry {:totp/uuid (or (some->
                                                           location
                                                           (get-in [:location/query-params :uuid])
                                                           (parse-uuid))
                                                         (random-uuid))}]
               [:data/query core/totp-query]])
   :render #'render})
